import { environment } from '@env/environment';
import type { ErrorEvent } from '@sentry/angular';

// A frame is ours only when it is a .js file from our bundles: the page's own or the embedded
// booking widget's, which has no Sentry client of its own and reports through this one.
// Page-URL frames share the origin but are not .js, which is where injected inline scripts land.
// document.baseURI, not location.origin: the bundles load relative to the absolute <base href>.
function isOwnBundleFrame(filename: string | undefined): boolean {
  try {
    const url = new URL(filename ?? '');
    const ownOrigins = [new URL(document.baseURI).origin, new URL(environment.webcomponentWidgetUrl).origin];
    return ownOrigins.includes(url.origin) && url.pathname.endsWith('.js');
  } catch {
    return false;
  }
}

// Chrome iOS and the Google app inject a script that recurses into a stack overflow. Its frames
// are the page URL or missing, never our bundles, so a recursion in our own code is still reported.
export function isGoogleIosInjectedRecursion(event: ErrorEvent): boolean {
  const exceptions = event.exception?.values ?? [];
  if (!exceptions.some(exception => exception.value?.includes('Maximum call stack size exceeded'))) {
    return false;
  }
  const userAgent = event.request?.headers?.['User-Agent'] ?? globalThis.navigator?.userAgent ?? '';
  if (!/\b(CriOS|GSA)\//.test(userAgent)) {
    return false;
  }
  return !exceptions.some(exception =>
    (exception.stacktrace?.frames ?? []).some(frame => isOwnBundleFrame(frame.filename))
  );
}
